import { useContext, useState } from "react";
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Button,
  TextField,
  Link,
  Box,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  alpha,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AuthContext from "../context/AuthContext";
import logo from "../logo.png";

const LoginPage = () => {
  const { loginUser, backendUrl } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [email, setEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const username = data.get("username");
    const password = data.get("password");

    setLoading(true);
    setError(null);
    setEmailNotVerified(false);
    setResendSuccess(false);
    setEmail(username);

    try {
      await loginUser(username, password);
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true);
        setError("Your email must be verified to login. Please check your email for the verification link.");
      } else {
        setError(err.message || "Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      const response = await fetch(`${backendUrl}api/auth/resend-verification/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send verification email');
      }

      setResendSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
    <SEO title="Log In - Couplendar" />
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: '#F9F7F4',
      py: { xs: 2, sm: 3, md: 8 }
    }}>
      <Container maxWidth="sm">
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.75, sm: 1 },
            mb: { xs: 2, sm: 3, md: 4 },
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        >
          <img
            src={logo}
            alt="Couplendar"
            style={{
              width: window.innerWidth < 600 ? 36 : 48,
              height: window.innerWidth < 600 ? 36 : 48,
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              color: '#7B6B8D',
            }}
          >
            Couplendar
          </Typography>
        </Box>

        <Card
          sx={{
            background: 'white',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            borderRadius: 3,
            border: 'none'
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
            <Typography
              variant="h5"
              textAlign="center"
              sx={{
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600,
                color: '#333'
              }}
            >
              Sign In
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  py: { xs: 0.5, sm: 1 },
                  '& .MuiAlert-icon': {
                    color: '#C4856A'
                  },
                  whiteSpace: 'pre-line'
                }}
              >
                {error}
              </Alert>
            )}

            {resendSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  py: { xs: 0.5, sm: 1 },
                  '& .MuiAlert-icon': {
                    color: '#A8D5BA'
                  }
                }}
              >
                Verification email sent! Please check your inbox. The link is valid for 5 minutes.
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={{ xs: 1.75, sm: 2, md: 2.5 }}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="Email"
                  name="username"
                  autoComplete="email"
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused fieldset': {
                        borderColor: '#7B6B8D'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#7B6B8D'
                    }
                  }}
                />
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused fieldset': {
                        borderColor: '#7B6B8D'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#7B6B8D'
                    }
                  }}
                />

                {emailNotVerified && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    sx={{
                      borderRadius: 2,
                      py: { xs: 1.25, sm: 1.5 },
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      borderColor: '#7B6B8D',
                      color: '#7B6B8D',
                      '&:hover': {
                        borderColor: '#6A5A7A',
                        background: alpha('#7B6B8D', 0.06)
                      }
                    }}
                  >
                    {resendLoading ? <CircularProgress size={24} /> : 'Resend Verification Email'}
                  </Button>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                    background: '#7B6B8D',
                    boxShadow: 'none',
                    '&:hover': {
                      background: '#6A5A7A',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
                </Button>
              </Stack>

              <Box textAlign="center" mt={{ xs: 2, sm: 2.5, md: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9B9B9B',
                    mb: { xs: 0.5, sm: 0.75, md: 1 },
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                  }}
                >
                  <Link
                    href="/forgot-password"
                    sx={{
                      color: '#7B6B8D',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Forgot password?
                  </Link>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9B9B9B',
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                  }}
                >
                  Don't have an account?{' '}
                  <Link
                    href="/register"
                    sx={{
                      color: '#7B6B8D',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Sign up
                  </Link>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, mt: 1.5 }}>
                  <Link href="/terms-of-service" sx={{ color: '#9B9B9B', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: '#7B6B8D', textDecoration: 'underline' } }}>
                    Terms of Service
                  </Link>
                  <Link href="/privacy-policy" sx={{ color: '#9B9B9B', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: '#7B6B8D', textDecoration: 'underline' } }}>
                    Privacy Policy
                  </Link>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
    </>
  );
}

export default LoginPage;
